import { get } from 'svelte/store';

import type { EstimationRouteType, RouteAlertsType } from '../../AssignmentApi';
import { AssignmentJobApi } from '../../AssignmentJobApi';
import { RouteSummaryData, type RouteSummaryDataRouteTypeType } from './RouteSummaryData';
import { AssignmentApi } from '../../AssignmentApi';
import { api } from '../../../../../stores/api';
import { Assignment } from '../../Assignment';
import { RouteInfoUtils } from './RouteInfoUtils';
import {
    PlanningDeliveryRouteComparision,
    type RouteComparisionType,
} from '../PlanningDeliveryRouteComparision';
import { RouteSummaryAction } from './RouteSummaryAction';
import type { AssignmentJob } from '../../AssignmentJob';
import { getFilterAssignmentId } from '../../getFilterAssignmentId';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { Api } from '@automapa/svelte-automapa-template';
import { debug } from '../../../../../stores/store';
import { dla } from '../DeliveryListApp/dla';

export class RouteSummaryApp {
    static ROUTE_MODIFIED_BY_OPERATOR_DISCARD = 'routeModifiedByOperatorDiscard';
    static ROUTE_MODIFIED_BY_OPERATOR_COMMIT = 'routeModifiedByOperatorCommit';
    static ROUTE_MODIFIED_BY_OPERATOR_RECALCULATE = 'routeModifiedByOperatorRecalculate';
    static ROUTE_MODIFIED_BY_OPERATOR_REVERSE = 'routeModifiedByOperatorReverse';

    private readonly routes: RouteSummaryData[] = [];
    private actions: RouteSummaryAction[] = [];
    public selectedRouteType: RouteSummaryDataRouteTypeType = 'to_confirm'; // , 'base_route'];
    private rowsVisibilityCallback: (selectedRouteType: RouteSummaryDataRouteTypeType) => void =
        () => {};

    private assignment: Assignment | null = null;
    private tabRoute: boolean | null = null;

    constructor() {
        this.routes = [];
        this.addRoute(new RouteSummaryData('base_route'));
        this.addRoute(new RouteSummaryData('to_confirm'));
        this.addRoute(new RouteSummaryData('to_assign'));
        this.addRoute(new RouteSummaryData('current_in_realisation'));
        this.addRoute(new RouteSummaryData('modified_by_operator'));
        this.getModifiedByOperatorRoute().title = 'changed';
        this.getToAssignRoute().title = 'to_assign';
        this.getToConfirmRoute().title = 'to_confirm';
        this.getCurrentRoute().title = 'in_realisation';
        this.getBaseRoute().title = 'to_send';
    }

    public setListener(id: string, action: () => Promise<void>) {
        // remove id action if exists
        this.actions = this.actions.filter(a => a.getId() !== id);

        // id already exists return
        if (this.actions.find(a => a.getId() === id)) {
            console.warn(`Action ${id} already exists`);
            return;
        }
        this.actions.push(new RouteSummaryAction(id, action));
    }

    public clearListeners() {
        this.actions = [];
    }

    public executeAction(id: string, onFinish: () => void = () => {}) {
        let action = this.actions.find(a => a.getId() === id);
        if (action) {
            action.execute(onFinish);
        } else {
            console.warn(`Action ${id} not found`);
        }
    }

    private addRoute(route: RouteSummaryData) {
        this.routes.push(route);
    }

    reset() {
        this.setSelectedRouteType('to_confirm');
        this.routes.forEach(route => {
            route.reset();
        });
    }

    getRoutes() {
        return this.routes;
    }

    getBaseRoute(): RouteSummaryData {
        let res = this.routes.find(route => route.routeType === 'base_route');
        if (res) {
            return res;
        }
        throw new Error('base_route route not found');
    }

    getToConfirmRoute(): RouteSummaryData {
        let res = this.routes.find(route => route.routeType === 'to_confirm');
        if (res) {
            return res;
        }
        throw new Error('to_confirm route not found');
    }

    getToAssignRoute(): RouteSummaryData {
        let res = this.routes.find(route => route.routeType === 'to_assign');
        if (res) {
            return res;
        }
        throw new Error('to_confirm route not found');
    }

    getCurrentRoute(): RouteSummaryData {
        let res = this.routes.find(route => route.routeType === 'current_in_realisation');
        if (res) {
            return res;
        }
        throw new Error('current_in_realisation route not found');
    }

    getModifiedByOperatorRoute(): RouteSummaryData {
        let res = this.routes.find(route => route.routeType === 'modified_by_operator');
        if (res) {
            return res;
        }
        throw new Error('modified_by_operator route not found');
    }

    // getAnotherRoutePriceDiff(calcFrom: RouteSummaryDataRouteTypeType = 'base_route') {
    //     let baseRoute = this.getBaseRoute();
    //     let anotherRoute = this.getToConfirmRoute();
    //     let currentRoute = this.getCurrentRoute();
    //     if (baseRoute.cost !== null && anotherRoute.cost !== null && calcFrom === 'base_route') {
    //         return anotherRoute.cost - baseRoute.cost;
    //     }
    //     if (
    //         currentRoute &&
    //         currentRoute.cost !== null &&
    //         anotherRoute.cost !== null &&
    //         calcFrom === 'current_in_realisation'
    //     ) {
    //         return anotherRoute.cost - currentRoute.cost;
    //     }
    //     return null;
    // }

    isCurrentRouteVisible() {
        return this.getCurrentRoute().quantity !== null;
    }

    isBaseRouteComparable() {
        let cr = this.getCurrentRoute();
        let br = this.getBaseRoute();
        return (
            cr.quantity !== null &&
            br.quantity !== null &&
            (cr.estimateKey !== br.estimateKey ||
                cr.quantity !== br.quantity ||
                cr.isEstimated !== br.isEstimated ||
                cr.distanceMeters !== br.distanceMeters ||
                cr.durationSeconds !== br.durationSeconds)
        );
    }

    getRouteCostDiff(route2: RouteSummaryDataRouteTypeType) {
        let route1: RouteSummaryDataRouteTypeType = 'base_route';
        if (this.getCurrentRoute()?.quantity !== null) {
            route1 = 'current_in_realisation';
        }
        let route1Data = this.getRouteData(route1);
        let route2Data = this.getRouteData(route2);
        if (
            route1Data === null ||
            route1Data.cost === null ||
            route2Data === null ||
            route2Data.cost === null
        ) {
            return null;
        }
        return route2Data.cost - route1Data.cost;
    }

    getRouteDistanceDiff(routeType: RouteSummaryDataRouteTypeType) {
        let route1: RouteSummaryDataRouteTypeType = 'base_route';
        if (this.getCurrentRoute()?.quantity !== null) {
            route1 = 'current_in_realisation';
        }
        let route1Data = this.getRouteData(route1);
        let route2Data = this.getRouteData(routeType);
        if (
            route1Data === null ||
            route1Data.distanceMeters === null ||
            route2Data === null ||
            route2Data.distanceMeters === null
        ) {
            return null;
        }
        return route2Data.distanceMeters - route1Data.distanceMeters;
    }

    getRouteDurationDiff(routeType: RouteSummaryDataRouteTypeType) {
        let route1: RouteSummaryDataRouteTypeType = 'base_route';
        if (this.getCurrentRoute()?.quantity !== null) {
            route1 = 'current_in_realisation';
        }
        let route1Data = this.getRouteData(route1);
        let route2Data = this.getRouteData(routeType);
        if (
            route1Data === null ||
            route1Data.durationSeconds === null ||
            route2Data === null ||
            route2Data.durationSeconds === null
        ) {
            return null;
        }
        return route2Data.durationSeconds - route1Data.durationSeconds;
    }

    getRouteData(routeType: RouteSummaryDataRouteTypeType): RouteSummaryData {
        let res = this.routes.find(route => route.routeType === routeType);
        if (res) {
            return res;
        }
        throw new Error('route not found');
    }

    setBaseLoading(isLoading: boolean) {
        this.getBaseRoute().isLoading = isLoading;
    }

    setMaximumLoading(isLoading: boolean) {
        this.getToConfirmRoute().isLoading = isLoading;
    }

    setStopsQuantity(stops: number | null) {
        this.getBaseRoute().stops = stops;
        if (this.assignment && !this.assignment.getRouteInfo()?.current) {
            this.getCurrentRoute().stops = stops;
        }
    }

    importBaseRouteFromAssignment(assignment: Assignment, assignmentJobs: AssignmentJob[] = []) {
        let route = this.getBaseRoute();
        route.title = 'to_send';
        if (assignment.isRouteCalculated()) {
            route.estimateKey = null;
            route.isEstimated = false;
            route.distanceMeters = assignment.getRouteDistance('m');
            route.durationSeconds = assignment.getRouteDuration('s', true);
            if (assignment.getRouteInfo()?.route?.count_jobs) {
                route.quantity = assignment.getRouteInfo()?.route?.count_jobs ?? -2;
            } else if (assignment.getRouteInfo()?.estimate?.count_jobs) {
                route.quantity = assignment.getRouteInfo()?.estimate?.count_jobs ?? -2;
            } else {
                route.quantity =
                    assignment.getCountDeliveries() + assignment.getCountRealisations();
            }
            route.tolls = assignment.getRouteTolls();
            route.costDetails = {};
            route.cost = RouteInfoUtils.getCostByDistanceAndQuantity(
                route.distanceMeters,
                route.quantity,
                route.durationSeconds,
                route.tolls,
                assignment.getVehicle(),
                route.costDetails
            );
            route.alerts = this.getCountAlerts(assignment.getRouteInfo()?.alerts, assignment);
        } else if (assignment.hasEstimation()) {
            route.isEstimated = true;
            route.distanceMeters = assignment.getRouteEstimatedDistance('m');
            route.durationSeconds = assignment.getRouteEstimatedDuration('s');
            route.quantity = assignment.getRouteInfo()?.estimate?.count_jobs ?? -2;
            route.costDetails = {};
            route.tolls = assignment.getRouteEstimatedTolls();
            route.cost = RouteInfoUtils.getCostByDistanceAndQuantity(
                route.distanceMeters,
                route.quantity,
                route.durationSeconds,
                route.tolls,
                assignment.getVehicle(),
                route.costDetails
            );
            route.estimateKey = assignment.getRouteEstimatedKey();
            route.alerts = null;
        } else {
            route.quantity = null;
            route.distanceMeters = null;
            route.durationSeconds = null;
            route.cost = null;
            route.alerts = null;
        }
        this.importCurrentRouteFromAssignment(assignment);

        if (0 && assignmentJobs.length > 0) {
            route.timeWindowsExceeded = this.getCountTimeWindowsExceeded(
                assignment,
                assignmentJobs,
                true
            );
        }
        route.isLoading = false;
    }

    importMaximumRouteFromAssignment(assignment: Assignment) {
        let route = this.getToConfirmRoute();
        route.title = 'to_approve';
        let alternative = assignment.getEstimationAlternative();
        if (alternative) {
            route.isEstimated = true;
            route.quantity = alternative.count_jobs;
            route.distanceMeters = alternative.distance_m;
            route.durationSeconds =
                alternative.eta_with_stops_m !== null ? alternative.eta_with_stops_m * 60 : null;
            route.tolls = alternative.tolls ?? null;
            route.costDetails = {};
            route.cost = RouteInfoUtils.getCostByDistanceAndQuantity(
                route.distanceMeters,
                route.quantity,
                route.durationSeconds,
                route.tolls,
                assignment.getVehicle(),
                route.costDetails
            );
            route.estimateKey = alternative.estimate_key;
        } else {
            route.quantity = null;
            route.distanceMeters = null;
            route.durationSeconds = null;
            route.cost = null;
            route.estimateKey = null;
        }
        this.importCurrentRouteFromAssignment(assignment);
        route.isLoading = false;
    }

    importCurrentRouteFromAssignment(assignment: Assignment) {
        let route = this.getCurrentRoute();
        if (route === null) {
            console.debug('$rsa, Route is null');
            return;
        }
        if (assignment.getRouteInfo()?.current === null) {
            console.debug('$rsa, No current route info');
            return;
        }
        route.title = 'in_progress';
        let current = assignment.getRouteInfo()?.current;
        if (current) {
            let quantity = current.count_jobs || RouteInfoUtils.getQuantity(current);
            route.isEstimated = false;
            route.quantity = quantity;
            route.distanceMeters = current.distance_m;
            route.durationSeconds =
                current.eta_with_stops_m !== null ? current.eta_with_stops_m * 60 : null;
            route.tolls = current.tolls ?? null;
            // route.cost = RouteInfoUtils.getCost(current);
            route.costDetails = {};
            route.cost = RouteInfoUtils.getCostByDistanceAndQuantity(
                route.distanceMeters,
                route.quantity,
                route.durationSeconds,
                route.tolls,
                assignment.getVehicle(),
                route.costDetails
            );
            route.estimateKey = null;
            route.alerts = this.getCountAlerts(current.alerts, assignment);
            if (current.completed) {
                route.title = 'route_completed';
            }
            route.stops = (current.points?.length ? current.points.length - 2 : null);
        } else if (assignment.isLocked()) {
            route.isEstimated = false;
            route.durationSeconds = assignment.getRouteDuration('s');
            route.distanceMeters = assignment.getRouteDistance('m');
            route.quantity =
                assignment.getRouteInfo()?.route?.count_jobs ??
                assignment.getRouteInfo()?.estimate?.count_jobs ??
                null;
            if (route.quantity === null) {
                route.quantity =
                    assignment.getRouteInfo()?.realisation_status?.route_points?.length ?? null;
                if (route.quantity) route.quantity -= 2;
            }
            route.tolls = assignment.getRouteTolls();
            // route.quantity = RouteInfoUtils.getQuantity(current);
            route.costDetails = {};
            route.cost = RouteInfoUtils.getCostByDistanceAndQuantity(
                route.distanceMeters,
                route.quantity,
                route.durationSeconds,
                route.tolls,
                assignment.getVehicle(),
                route.costDetails
            );
            route.estimateKey = null;
            if (route.isEstimated) {
                route.estimateKey = assignment.getRouteInfo()?.estimate?.estimate_key ?? null;
            }
            route.alerts = this.getCountAlerts(assignment.getRouteInfo()?.alerts, assignment);
            if (assignment.getRouteInfo()?.route?.completed) {
                route.title = 'route_completed';
            } else if (assignment.getRouteInfo()?.route?.downloaded) {
                route.title = 'in_progress';
            } else {
                route.title = 'route_sent';
            }
        } else {
            route.quantity = null;
            route.distanceMeters = null;
            route.durationSeconds = null;
            route.cost = null;
            route.estimateKey = null;
            route.alerts = null;
        }
        route.isLoading = false;
    }

    async importToAssignRoute(api: Api, assignment: Assignment, day: string) {
        let route = this.getToAssignRoute();
        let q = getFilterAssignmentId(true, true, assignment.isDefault(), assignment.getId(), day);
        let f = new ApiFilterBuilder(null);
        f.setProjectionFields(['job_id']);
        f.setRelations(['job']);
        let items = await AssignmentJobApi.getCountWithQueries(api, q, f).catch(e => {
            console.error('importToAssignRoute', e);
            route.isLoading = false;
            return { count: 0 };
        });
        route.setQuantity(items.count);
        route.title = 'to_assign';
        route.isEstimated = true;
        route.isLoading = false;
    }

    stopLoading(): this {
        this.setBaseLoading(false);
        this.setMaximumLoading(false);
        this.getToAssignRoute().isLoading = false;
        this.getToConfirmRoute().isLoading = false;
        return this;
    }

    startLoading(): this {
        this.setBaseLoading(true);
        this.setMaximumLoading(true);
        this.getToAssignRoute().isLoading = true;
        this.getToConfirmRoute().isLoading = true;
        return this;
    }

    isLoading(): boolean {
        return this.getBaseRoute().isLoading || this.getToConfirmRoute().isLoading;
    }

    async reload(assignment: Assignment, day: string | null = null) {
        this.startLoading();
        this.assignment = assignment;
        if (
            !assignment.isRouteCalculated() &&
            assignment.getId() > 0 &&
            assignment.hasVehicleAssigned() &&
            assignment.getCountDeliveries() > 0
        ) {
            let estimate: EstimationRouteType | null = await AssignmentApi.getEstimation(
                get(api),
                assignment.getId()
            );
            if (estimate) {
                assignment.setEstimation(estimate);
            }
        } else {
            assignment.setEstimation(null);
        }

        if (assignment) {
            this.importBaseRouteFromAssignment(assignment, get(dla).getItems());
            this.setBaseLoading(false);
        }

        let estimateWithSuggestions: EstimationRouteType | null = null;
        if (assignment.getId() > 0 && assignment.hasVehicleAssigned()) {
            //let response = await AssignmentJobApi.getIds(get(api), assignment, true, false);
            //let allIds: number[] = response.map((a: any) => a.job_id);
            estimateWithSuggestions = await AssignmentApi.getEstimation(
                get(api),
                assignment.getId(),
                true,
                assignment.hasRouteBeenEverDownloaded() && !assignment.isRealised(),
                null //allIds
            );
        }
        if (estimateWithSuggestions && assignment) {
            assignment.setEstimationAlternative(estimateWithSuggestions);
        }
        if (assignment) {
            this.importMaximumRouteFromAssignment(assignment);
            this.setMaximumLoading(false);
        }

        if (assignment && day !== null) {
            await this.importToAssignRoute(get(api), assignment, day);
        } else {
            console.warn('importToAssignRoute, day is null or assignment is null', assignment, day);
        }

        if (this.tabRoute !== null) {
            this.setRowsVisibility(assignment, this.tabRoute);
        }

        this.stopLoading();

        return assignment;
    }

    shouldToConfirmRouteBeVisible(tabRoute: boolean = true) {
        let toConfirmQuantity = this.getToConfirmRoute().quantity;
        let baseQuantity = this.getBaseRoute().quantity;
        return (
            (toConfirmQuantity === null ||
                baseQuantity === null ||
                toConfirmQuantity > baseQuantity) &&
            !tabRoute
        );
    }

    shouldRouteToAssignBeVisible(tabRoute: boolean = true) {
        let toAssignQuantity = this.getToAssignRoute().quantity;
        let baseQuantity = this.getBaseRoute().quantity;
        return (
            (toAssignQuantity === null ||
                baseQuantity === null ||
                toAssignQuantity > baseQuantity) &&
            !tabRoute
        );
    }

    importRouteModifiedByOperator(
        assignment: Assignment,
        assignmentJobs: AssignmentJob[] = []
    ): void {
        let pdrc: PlanningDeliveryRouteComparision = new PlanningDeliveryRouteComparision(
            assignment
        );
        let comparisionData: RouteComparisionType = pdrc.getComparisonData();
        let route = this.getModifiedByOperatorRoute();
        route.title = 'changed';
        route.isEstimated = false;
        route.quantity = this.getBaseRoute().quantity;
        route.distanceMeters = pdrc.getModifiedRouteData()?.distance_m ?? null;
        route.durationSeconds = pdrc.getModifiedRouteData()?.eta_with_stops_m
            ? pdrc.getModifiedRouteData()!.eta_with_stops_m * 60
            : null;
        route.tolls = pdrc.getModifiedRouteData()?.tolls ?? null;
        route.costDetails = {};
        route.cost = RouteInfoUtils.getCostByDistanceAndQuantity(
            route.distanceMeters,
            route.quantity,
            route.durationSeconds,
            route.tolls,
            assignment.getVehicle(),
            route.costDetails
        );
        route.estimateKey = null;
        route.stops = this.getBaseRoute().stops;
        if (assignmentJobs.length > 0) {
            route.timeWindowsExceeded = this.getCountTimeWindowsExceeded(
                assignment,
                assignmentJobs
            );
        } else {
            route.timeWindowsExceeded = null;
        }
        route.alerts = this.getCountAlerts(pdrc.getModifiedRouteData()?.alerts, assignment);
    }

    private getCountTimeWindowsExceeded(
        assignment: Assignment,
        assignmentJobs: AssignmentJob[],
        getBaseOnly: boolean = false
    ) {
        let countWindowsExceeded = 0;
        assignmentJobs.forEach(aj => {
            let timeWindow = assignment.getTimeWindow(aj, getBaseOnly);
            if (timeWindow && timeWindow.isValid() && timeWindow.isEtaExists()) {
                if (!timeWindow.isFulfilled()) countWindowsExceeded++;
            }
        });
        return countWindowsExceeded;
    }

    private getCountAlerts(alerts: RouteAlertsType | undefined, assignment: Assignment) {
        if (alerts) {
            let counts = {};
            for (let type in alerts) {
                let alertArray =
                    'length' in alerts[type] ? alerts[type] : Object.values(alerts[type]);
                counts[type] = alertArray.filter(a => !a.fulfilled).length;
            }
            return counts;
        }
        return undefined;
    }

    setRowsVisibilityCallback(fn: () => {}) {
        this.rowsVisibilityCallback = fn;
    }

    async setRowsVisibility(
        assignment: Assignment,
        tabRoute: boolean | null = null,
        selectedRouteType: RouteSummaryDataRouteTypeType | null = null,
        modifySelectedRouteTypeWhenNeeded: boolean = true
    ): Promise<void> {
        if (tabRoute !== null) {
            this.tabRoute = tabRoute;
        }
        this.getCurrentRoute().visible =
            assignment.isLocked() || assignment.isRouteModifiedAfterDownload();
        this.getBaseRoute().visible =
            !assignment.isLocked() || assignment.isRouteModifiedAfterDownload();
        this.getToConfirmRoute().visible = this.shouldToConfirmRouteBeVisible(tabRoute);
        this.getToAssignRoute().visible = this.shouldRouteToAssignBeVisible(tabRoute);
        this.getModifiedByOperatorRoute().visible =
            (assignment.isRouteOrderModified() || this.getModifiedByOperatorRoute().isLoading) &&
            tabRoute;

        // console.log(
        //     'modified_by_operator',
        //     assignment.isRouteOrderModified(),
        //     this.getModifiedByOperatorRoute().isLoading
        // );

        if (selectedRouteType !== null) {
            this.selectedRouteType = selectedRouteType;
        }

        if (modifySelectedRouteTypeWhenNeeded) {
            // https://er.automapa.pl/issues/41797
            if (
                this.selectedRouteType === 'modified_by_operator' &&
                !this.getModifiedByOperatorRoute().visible
            ) {
                this.selectedRouteType = 'base_route';
            }

            if (
                this.selectedRouteType === 'current_in_realisation' &&
                !this.getCurrentRoute().visible
            ) {
                this.selectedRouteType = 'base_route';
            }

            if (this.selectedRouteType === 'to_assign' && !this.getToAssignRoute().visible) {
                this.selectedRouteType = 'base_route';
            }

            if (this.selectedRouteType === 'to_confirm' && !this.getToConfirmRoute().visible) {
                this.selectedRouteType = 'base_route';
            }

            if (this.selectedRouteType === 'base_route' && !this.getBaseRoute().visible) {
                this.selectedRouteType = 'current_in_realisation';
            }

            await this.rowsVisibilityCallback(this.selectedRouteType);
        }
    }

    setSelectedRouteType(selectedRouteType: RouteSummaryDataRouteTypeType) {
        if (selectedRouteType != this.selectedRouteType) {
            this.selectedRouteType = selectedRouteType;
        }
    }

    isButtonConfirmAllShouldBeVisible(): boolean {
        let route = this.getToConfirmRoute();
        let routeBase = this.getBaseRoute();
        return (
            route &&
            route.visible &&
            route?.quantity !== null &&
            route?.quantity > 0 &&
            (routeBase.quantity === null || route.quantity > routeBase.quantity) &&
            !route.isLoading &&
            !this.assignment?.isLocked() &&
            !this.assignment?.hasRouteBeenEverDownloaded()
        );
    }

    getAssignmentId(): number | null {
        return this.assignment?.getId() ?? null;
    }
}
